import { useEffect, type RefObject } from "react";

/** Tags whose elements count as "another input is focused" */
const INPUT_TAGS = new Set(["INPUT", "TEXTAREA", "SELECT"]);

/** A single printable character typed without a modifier — the only kind we redirect. */
function isPlainPrintableKey(e: globalThis.KeyboardEvent): boolean {
  // Ignore modifier-only keys, navigation, and shortcuts
  if (e.ctrlKey || e.metaKey || e.altKey) return false;
  // `e.key` can be undefined for synthetic / programmatically-dispatched
  // keydown events and some IME composition events.
  return typeof e.key === "string" && e.key.length === 1;
}

/** Elements Space activates natively (Radix checkboxes, switches, tabs… are buttons). */
const SPACE_ACTIVATED_SELECTOR =
  'button, summary, [role="button"], [role="checkbox"], [role="switch"], [role="radio"], [role="tab"], [role="menuitem"], [role="menuitemcheckbox"], [role="menuitemradio"], [role="option"]';

/**
 * Space on a focused button or checkbox is that control's activation key, not
 * typing: redirecting it to the chat input would swallow the click.
 */
export function isSpaceActivatingFocusedControl(e: globalThis.KeyboardEvent): boolean {
  if (e.key !== " ") return false;
  const active = document.activeElement;
  return !!active && active !== document.body && active.matches(SPACE_ACTIVATED_SELECTOR);
}

/** Something on the page already owns the keyboard. */
function keyboardIsClaimed(): boolean {
  // WHA-3664: on the 3D viewer (/buildings/:id/model) WASD/arrow keys drive
  // the camera — don't hijack keystrokes into the chat. Read the path at
  // event time (not via useLocation) so the component stays Router-agnostic.
  if (window.location.pathname.endsWith("/model")) return true;

  const active = document.activeElement;
  if (!active) return false;
  return (
    INPUT_TAGS.has(active.tagName) ||
    (active as HTMLElement).isContentEditable ||
    !!active.closest('[role="dialog"], [role="alertdialog"]')
  );
}

/**
 * Start typing anywhere on the page and the keystrokes land in the chat pill —
 * unless another input owns the focus, or the page needs the keys itself.
 */
export function useTypeToFocus(inputRef: RefObject<HTMLInputElement>): void {
  useEffect(() => {
    function handleGlobalKeyDown(e: globalThis.KeyboardEvent) {
      if (
        e.defaultPrevented ||
        e.isComposing ||
        !isPlainPrintableKey(e) ||
        isSpaceActivatingFocusedControl(e) ||
        keyboardIsClaimed()
      )
        return;
      inputRef.current?.focus();
    }

    document.addEventListener("keydown", handleGlobalKeyDown);
    return () => document.removeEventListener("keydown", handleGlobalKeyDown);
  }, [inputRef]);
}
